import {
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import dayjs from 'dayjs';
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
  type RegisterOptions,
} from 'react-hook-form';
import { Check, X } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

import { Button } from '@/components/ui/button';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  useComboboxAnchor,
} from '@/components/ui/combobox';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  TypeOptions,
  USCGResourceKindOptions,
} from 'components/IncidentConfig/ResourceTypes';
import {
  ResourceClassification,
  useCustomResources,
} from 'components/IAP/hooks/customResourceTypeHooks';
import { ResourceRequestItem } from 'components/IAP/hooks/incidentResourceRequestHooks';
import { useResourceSets } from 'components/IAP/hooks/incidentResourceHooks';
import {
  catalogResourceForInput,
  resourceInputText,
} from 'components/IAP/resourceCatalogName';
import { useHwcgFlag } from 'utils/featureFlags';
import {
  ICS213RR_PRIORITY_OPTIONS,
  ICS213RR_PRIORITY_ROUTINE,
  itemDescription,
  normalize213rrPriority,
} from 'components/IAP/ResourceRequests/ics213rr/ics213rrPriority';
import { NULL_VALUE } from 'constants/displayConstants';
import { ReportingLocationField } from './ReportingLocationField';

type AddRequestedResourceProps = {
  setCustomResources: Dispatch<SetStateAction<ResourceRequestItem[]>>;
  setLogisticsItems?: Dispatch<SetStateAction<ResourceRequestItem[][]>>;
  /**
   * When adding a new row, append this draft attach list instead of an empty
   * logistics slot (used when Attach is edited before save).
   */
  draftLogisticsItems?: ResourceRequestItem[];
  workAssignment?: string | null;
  setShowAdd: Dispatch<SetStateAction<boolean>>;
  showOwningOrg?: boolean;
  resourceToEdit?: ResourceRequestItem;
  editIndex?: number | null;
  onClose?: () => void;
  /** `row` is one order-table row. `card` is the owning-organization editor. */
  layout?: 'card' | 'row';
  trailingCells?: ReactNode;
  /** Attach column content for row layout (picker while adding/editing). */
  attachSlot?: ReactNode;
  /** Catalog id to store when the description is prefilled from an org asset. */
  initialResourceSetId?: string | null;
};

type Option = { value: string; label: string };

const CUSTOM = '__custom__';
const DOCUMENTATION_ENTRY = '__documentation_entry__';

const catalogId = (resource: { id: string; resource_set_id?: string | null }) =>
  resource.resource_set_id || resource.id;

const TIME_OPTIONS = Array.from({ length: 48 }, (_, index) => {
  const hour = String(Math.floor(index / 2)).padStart(2, '0');
  const minute = index % 2 === 0 ? '00' : '30';
  return `${hour}:${minute}`;
});

function TextControl({
  name,
  label,
  required,
  rules,
  bare,
}: {
  name: keyof ResourceRequestItem;
  label: string;
  required?: boolean;
  rules?: RegisterOptions<ResourceRequestItem>;
  bare?: boolean;
}) {
  const { control } = useFormContext<ResourceRequestItem>();
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => (
        <Field>
          {bare ? null : (
            <FieldLabel htmlFor={name}>
              {label}
              {required ? ' *' : ''}
            </FieldLabel>
          )}
          <Input
            id={name}
            aria-label={bare ? label : undefined}
            value={field.value == null ? '' : String(field.value)}
            onChange={field.onChange}
            onBlur={field.onBlur}
            aria-invalid={fieldState.invalid || undefined}
          />
          {fieldState.error?.message ? (
            <FieldError>{String(fieldState.error.message)}</FieldError>
          ) : null}
        </Field>
      )}
    />
  );
}

function OrgResourceDescriptionField({
  bare,
  label,
  onBlack = false,
}: {
  bare?: boolean;
  label: string;
  /** The ICS order table sits on jet black, so the value must be light. */
  onBlack?: boolean;
}) {
  const { control, setValue, watch } = useFormContext<ResourceRequestItem>();
  const description = watch('description') ?? '';
  const resourceSetId = watch('resource_set_id') ?? '';
  const { data: resourceSets = [], isLoading } = useResourceSets();
  const [query, setQuery] = useState(String(description));

  useEffect(() => {
    setQuery(resourceInputText(String(description), resourceSets));
  }, [description, resourceSets]);

  useEffect(() => {
    if (isLoading) return;
    const current = String(description);
    const picked = catalogResourceForInput(current, resourceSets);
    if (!picked || picked.name === current) return;
    setValue('description', picked.name, { shouldValidate: true });
    setValue('resource_set_id', catalogId(picked));
    setQuery(picked.name);
  }, [description, isLoading, resourceSets, setValue]);

  const trimmed = query.trim();
  const matches = useMemo(() => {
    const needle = trimmed.toLowerCase();
    const rows = needle
      ? resourceSets.filter((resource) =>
          resource.name.toLowerCase().includes(needle)
        )
      : resourceSets;
    return rows.slice(0, 50);
  }, [resourceSets, trimmed]);
  const exact = resourceSets.find(
    (resource) => resource.name.trim().toLowerCase() === trimmed.toLowerCase()
  );

  return (
    <Controller
      name="description"
      control={control}
      rules={{ required: 'Description is required' }}
      render={({ field, fieldState }) => {
        const commitText = (text: string) => {
          const next = text.trim();
          const match =
            catalogResourceForInput(next, resourceSets) ||
            resourceSets.find(
              (resource) =>
                resource.name.trim().toLowerCase() === next.toLowerCase()
            );
          if (match) {
            field.onChange(match.name);
            setValue('resource_set_id', catalogId(match));
            setQuery(match.name);
            return;
          }
          field.onChange(next);
          setValue('resource_set_id', '');
          setQuery(next);
        };

        return (
          <Field>
            {bare ? null : (
              <FieldLabel htmlFor="description">
                {label}
                {' *'}
              </FieldLabel>
            )}
            <Combobox
              value={resourceSetId || null}
              filter={null}
              inputValue={query}
              onInputValueChange={(next) =>
                setQuery(resourceInputText(String(next ?? ''), resourceSets))
              }
              onValueChange={(value) => {
                const pickedId = typeof value === 'string' ? value : '';
                if (!pickedId || pickedId === DOCUMENTATION_ENTRY) {
                  commitText(query);
                  return;
                }
                const picked = resourceSets.find(
                  (resource) => catalogId(resource) === pickedId
                );
                if (!picked) return;
                field.onChange(picked.name);
                setValue('resource_set_id', catalogId(picked));
                setQuery(picked.name);
              }}
            >
              <ComboboxInput
                id="description"
                aria-label={label}
                placeholder="Type a documentation entry or pick a resource"
                className={
                  onBlack
                    ? 'tw:w-full tw:min-w-64 tw:bg-black tw:text-white tw:[&_input]:!bg-transparent tw:[&_input]:!text-white tw:[&_input]:placeholder:!text-neutral-400'
                    : 'tw:w-full'
                }
                aria-invalid={fieldState.invalid || undefined}
                onBlur={() => {
                  field.onBlur();
                  if (isLoading) return;
                  if (
                    trimmed === String(description).trim() &&
                    !!resourceSetId === !!exact
                  ) {
                    return;
                  }
                  commitText(query);
                }}
              />
              <ComboboxContent className="dark" positionerClassName="tw:z-[60]">
                <ComboboxEmpty>No organization resources</ComboboxEmpty>
                <ComboboxList>
                  {trimmed && !exact ? (
                    <ComboboxItem value={DOCUMENTATION_ENTRY}>
                      Documentation entry: {trimmed}
                    </ComboboxItem>
                  ) : null}
                  {matches.map((resource) => (
                    <ComboboxItem
                      key={catalogId(resource)}
                      value={catalogId(resource)}
                    >
                      {resource.name}
                    </ComboboxItem>
                  ))}
                </ComboboxList>
              </ComboboxContent>
            </Combobox>
            {fieldState.error?.message ? (
              <FieldError>{String(fieldState.error.message)}</FieldError>
            ) : null}
          </Field>
        );
      }}
    />
  );
}

function DetailedItemDescriptionField({
  bare,
  label,
  onBlack = false,
}: {
  bare?: boolean;
  label: string;
  onBlack?: boolean;
}) {
  const hwcg = useHwcgFlag();
  if (!hwcg) {
    return (
      <TextControl
        name="description"
        label={label}
        bare={bare}
        required
        rules={{ required: 'Description is required' }}
      />
    );
  }
  return (
    <OrgResourceDescriptionField bare={bare} label={label} onBlack={onBlack} />
  );
}

function CreatableCombobox({
  name,
  label,
  options,
  customLabel,
  placeholder,
  disabled,
  required,
  rules,
  bare,
}: {
  name: keyof ResourceRequestItem;
  label: string;
  options: Option[];
  customLabel: string;
  placeholder: string;
  disabled?: boolean;
  required?: boolean;
  rules?: RegisterOptions<ResourceRequestItem>;
  bare?: boolean;
}) {
  const { control } = useFormContext<ResourceRequestItem>();
  const anchor = useComboboxAnchor();
  const [customMode, setCustomMode] = useState(false);
  const [customText, setCustomText] = useState('');
  const [pending, setPending] = useState<Option[]>([]);
  const choices = [...options, ...pending];

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => {
        const current = field.value == null ? '' : String(field.value);
        const selected =
          choices.find((option) => option.value === current) ??
          (current ? { value: current, label: `${current} (Custom)` } : null);
        const items: Option[] = [
          { value: CUSTOM, label: customLabel },
          ...(selected &&
          !choices.some((option) => option.value === selected.value)
            ? [selected]
            : []),
          ...choices,
        ];

        const commitCustom = () => {
          const next = customText.trim();
          if (!next) return;
          setPending((previous) => [
            ...previous,
            { value: next, label: `${next} (Custom)` },
          ]);
          field.onChange(next);
          setCustomText('');
          setCustomMode(false);
        };

        return (
          <Field>
            {bare ? null : (
              <FieldLabel htmlFor={name}>
                {label}
                {required ? ' *' : ''}
              </FieldLabel>
            )}
            {customMode ? (
              <div className="tw:flex tw:gap-2">
                <Input
                  id={name}
                  value={customText}
                  aria-label={customLabel}
                  onChange={(event) => setCustomText(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      commitCustom();
                    }
                  }}
                />
                <Button type="button" size="sm" onClick={commitCustom}>
                  Add
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setCustomText('');
                    setCustomMode(false);
                  }}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <Combobox
                items={items}
                value={selected}
                disabled={disabled}
                itemToStringValue={(item) => (item ? item.label : '')}
                filter={(item, query) =>
                  item.value === CUSTOM ||
                  item.label.toLowerCase().includes(query.toLowerCase())
                }
                onValueChange={(next) => {
                  if (!next || next.value === CUSTOM) {
                    if (next?.value === CUSTOM) setCustomMode(true);
                    return;
                  }
                  field.onChange(next.value);
                }}
              >
                <div ref={anchor}>
                  <ComboboxTrigger
                    id={name}
                    aria-label={bare ? label : undefined}
                    disabled={disabled}
                    nativeButton={false}
                    render={
                      <Button
                        type="button"
                        variant="outline"
                        className="tw:w-full tw:justify-between"
                      />
                    }
                  >
                    <span className="tw:truncate">
                      {selected?.label ?? placeholder}
                    </span>
                  </ComboboxTrigger>
                </div>
                <ComboboxContent anchor={anchor} className="dark">
                  <ComboboxInput placeholder="Search..." showTrigger={false} />
                  <ComboboxEmpty>No matches</ComboboxEmpty>
                  <ComboboxList>
                    {(item: Option) => (
                      <ComboboxItem key={item.value} value={item}>
                        {item.label}
                      </ComboboxItem>
                    )}
                  </ComboboxList>
                </ComboboxContent>
              </Combobox>
            )}
            {fieldState.error?.message ? (
              <FieldError>{String(fieldState.error.message)}</FieldError>
            ) : null}
          </Field>
        );
      }}
    />
  );
}

function combineDateTime(date: string, time: string): string {
  if (!date) return '';
  const [hour, minute] = time.split(':').map((part) => Number(part));
  return dayjs(date)
    .hour(hour || 0)
    .minute(minute || 0)
    .second(0)
    .millisecond(0)
    .toISOString();
}

function ReportDateTime({ bare }: { bare?: boolean }) {
  const { control } = useFormContext<ResourceRequestItem>();
  const [draftTime, setDraftTime] = useState('00:00');
  return (
    <Controller
      name="requested_reporting_datetime"
      control={control}
      render={({ field }) => {
        const parsed =
          field.value && dayjs(field.value).isValid()
            ? dayjs(field.value)
            : null;
        const date = parsed ? parsed.format('YYYY-MM-DD') : '';
        const time = parsed ? parsed.format('HH:mm') : draftTime;
        const times = TIME_OPTIONS.includes(time)
          ? TIME_OPTIONS
          : [time, ...TIME_OPTIONS];
        return (
          <div
            className={
              bare
                ? 'tw:flex tw:min-w-0 tw:flex-col tw:gap-1'
                : 'tw:grid tw:grid-cols-1 tw:gap-3 tw:sm:grid-cols-2'
            }
          >
            <Field>
              {bare ? null : (
                <FieldLabel htmlFor="requested-report-date">
                  Requested Report Date
                </FieldLabel>
              )}
              <Input
                id="requested-report-date"
                type="date"
                aria-label={bare ? 'Requested report date' : undefined}
                value={date}
                onChange={(event) =>
                  field.onChange(combineDateTime(event.target.value, time))
                }
              />
            </Field>
            <Field>
              {bare ? null : (
                <FieldLabel htmlFor="requested-report-time">Time</FieldLabel>
              )}
              <Select
                value={time}
                onValueChange={(next) => {
                  const nextTime = String(next);
                  setDraftTime(nextTime);
                  if (date) field.onChange(combineDateTime(date, nextTime));
                }}
              >
                <SelectTrigger
                  id="requested-report-time"
                  aria-label={bare ? 'Time' : undefined}
                  className="tw:w-full"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="dark">
                  {times.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
        );
      }}
    />
  );
}

function PriorityField({ bare }: { bare?: boolean }) {
  const { control } = useFormContext<ResourceRequestItem>();
  return (
    <Controller
      name="priority"
      control={control}
      render={({ field }) => (
        <Field>
          {bare ? null : <FieldLabel htmlFor="priority">Priority</FieldLabel>}
          <Select
            value={
              normalize213rrPriority(field.value) || ICS213RR_PRIORITY_ROUTINE
            }
            onValueChange={(next) => field.onChange(String(next))}
          >
            <SelectTrigger
              id="priority"
              aria-label={bare ? 'Priority' : undefined}
              className="tw:w-full"
            >
              <SelectValue placeholder="Select..." />
            </SelectTrigger>
            <SelectContent className="dark">
              {ICS213RR_PRIORITY_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      )}
    />
  );
}

function useKindTypeOptions() {
  const { data: customKinds = [] } = useCustomResources(
    ResourceClassification.Kind
  );
  const { data: customTypes = [] } = useCustomResources(
    ResourceClassification.Type
  );
  const kindOptions: Option[] = [
    ...USCGResourceKindOptions.filter((option) => option.value !== 'Personnel'),
    ...customKinds.map((resource) => ({
      value: resource.name!,
      label: `${resource.name} (Custom)`,
    })),
  ];
  const typeOptions: Option[] = [
    ...TypeOptions,
    ...customTypes.map((resource) => ({
      value: resource.type!,
      label: `${resource.type} (Custom)`,
    })),
  ];
  return { kindOptions, typeOptions };
}

function KindAndType() {
  const { kindOptions, typeOptions } = useKindTypeOptions();

  return (
    <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:sm:grid-cols-2">
      <CreatableCombobox
        name="kind"
        label="Resource Kind"
        options={kindOptions}
        customLabel="+ Add Custom Kind"
        placeholder="Select a resource kind..."
        required
        rules={{ required: 'Resource kind is required' }}
      />
      <CreatableCombobox
        name="type"
        label="Type"
        options={typeOptions}
        customLabel="+ Add Custom Type"
        placeholder="Select a resource type..."
        required
        rules={{ required: 'Type is required' }}
      />
    </div>
  );
}

function ItemEditorRow({
  onSave,
  onCancel,
  trailingCells,
  attachSlot,
}: {
  onSave: () => void;
  onCancel: () => void;
  trailingCells?: ReactNode;
  attachSlot?: ReactNode;
}) {
  const { kindOptions, typeOptions } = useKindTypeOptions();

  return (
    <tr className="tw:align-top">
      <td className="tw:min-w-48 tw:px-1 tw:py-1">
        {attachSlot ?? (
          <span className="tw:text-muted-foreground">{NULL_VALUE}</span>
        )}
      </td>
      <td className="tw:min-w-20 tw:px-1 tw:py-1">
        <TextControl
          name="quantity"
          label="Quantity"
          bare
          required
          rules={{
            required: { value: true, message: 'This field is required' },
            pattern: {
              value: /^\d+$/,
              message: 'This field must be a positive integer',
            },
          }}
        />
      </td>
      <td className="tw:px-1 tw:py-1">
        <CreatableCombobox
          name="kind"
          label="Resource Kind"
          options={kindOptions}
          customLabel="+ Add Custom Kind"
          placeholder="Select a resource kind..."
          bare
          required
          rules={{ required: 'Resource kind is required' }}
        />
      </td>
      <td className="tw:px-1 tw:py-1">
        <CreatableCombobox
          name="type"
          label="Type"
          options={typeOptions}
          customLabel="+ Add Custom Type"
          placeholder="Select a resource type..."
          bare
          required
          rules={{ required: 'Type is required' }}
        />
      </td>
      <td className="tw:px-1 tw:py-1">
        <PriorityField bare />
      </td>
      <td className="tw:min-w-64 tw:px-1 tw:py-1">
        <DetailedItemDescriptionField
          bare
          onBlack
          label="Detailed item description"
        />
      </td>
      <td className="tw:min-w-[32rem] tw:px-1 tw:py-1">
        <ReportingLocationField bare />
      </td>
      <td className="tw:px-1 tw:py-1">
        <ReportDateTime bare />
      </td>
      {trailingCells}
      <td className="tw:px-1 tw:py-1 tw:text-right">
        <div className="tw:flex tw:justify-end tw:gap-1">
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            aria-label="Cancel item"
            onClick={onCancel}
          >
            <X className="tw:size-4" />
          </Button>
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            aria-label="Save item"
            onClick={onSave}
          >
            <Check className="tw:size-4" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

export const AddRequestedResource = ({
  setCustomResources,
  setLogisticsItems,
  draftLogisticsItems,
  workAssignment,
  setShowAdd,
  showOwningOrg = false,
  resourceToEdit,
  editIndex,
  onClose,
  layout = 'card',
  trailingCells,
  attachSlot,
  initialResourceSetId,
}: AddRequestedResourceProps) => {
  const initialDescription =
    itemDescription(resourceToEdit) || workAssignment || '';
  const initialValues = {
    ...resourceToEdit,
    name: initialDescription,
    description: initialDescription,
    priority: resourceToEdit?.priority || ICS213RR_PRIORITY_ROUTINE,
    resource_set_id:
      resourceToEdit?.resource_set_id || initialResourceSetId || '',
  };

  const form = useForm<ResourceRequestItem>({
    mode: 'onChange',
    defaultValues: initialValues,
  });

  useEffect(() => {
    if (resourceToEdit) return;
    form.setValue('name', workAssignment ?? '');
    form.setValue('description', workAssignment ?? '');
    form.setValue('resource_set_id', initialResourceSetId || '');
  }, [workAssignment, resourceToEdit, form, initialResourceSetId]);

  const closeForm = () => {
    if (onClose) {
      onClose();
      return;
    }
    setShowAdd(false);
  };

  const persistItem = (formData: ResourceRequestItem) => {
    const description = formData.description || formData.name || '';
    const resourceSetId = (formData.resource_set_id || '').trim();
    return {
      ...formData,
      name: description,
      description,
      type: formData.type || '',
      priority: formData.priority || ICS213RR_PRIORITY_ROUTINE,
      resource_set_id: resourceSetId || undefined,
    };
  };

  const onSubmit = async (formData: ResourceRequestItem) => {
    const next = persistItem(formData);
    if (editIndex !== null && editIndex !== undefined) {
      setCustomResources((prev) =>
        prev.map((resource, index) =>
          index === editIndex ? { ...next, id: resource.id } : resource
        )
      );
      form.reset({ name: '', description: '' });
      closeForm();
      return;
    }

    setCustomResources((prev) => [...prev, { ...next, id: uuidv4() }]);
    if (setLogisticsItems) {
      const attach =
        draftLogisticsItems !== undefined ? draftLogisticsItems : [];
      setLogisticsItems((prev) => [...prev, attach]);
    }
    form.reset({
      name: workAssignment ?? '',
      description: workAssignment ?? '',
    });
    closeForm();
  };

  if (layout === 'row') {
    return (
      <FormProvider {...form}>
        <ItemEditorRow
          onSave={form.handleSubmit(onSubmit)}
          onCancel={closeForm}
          trailingCells={trailingCells}
          attachSlot={attachSlot}
        />
      </FormProvider>
    );
  }

  return (
    <div className="tw:mb-2 tw:rounded-lg tw:border tw:border-border tw:bg-card tw:p-3">
      <FormProvider {...form}>
        <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:sm:grid-cols-12">
          <div className="tw:sm:col-span-1">
            <TextControl
              name="quantity"
              label="Quantity"
              required
              rules={{
                required: { value: true, message: 'This field is required' },
                pattern: {
                  value: /^\d+$/,
                  message: 'This field must be a positive integer',
                },
              }}
            />
          </div>
          <div className="tw:sm:col-span-4">
            <KindAndType />
          </div>
          <div className="tw:sm:col-span-1">
            <PriorityField />
          </div>
          <div className="tw:sm:col-span-6">
            <DetailedItemDescriptionField label="e. Detailed Item Description" />
          </div>
          <div className="tw:sm:col-span-2">
            <ReportingLocationField />
          </div>
          <div className="tw:sm:col-span-5">
            <ReportDateTime />
          </div>
          {showOwningOrg ? (
            <div className="tw:sm:col-span-4">
              <TextControl
                name="owning_organization"
                label="Owning Organization"
                required
                rules={{ required: 'Owning Organization is required' }}
              />
            </div>
          ) : null}
          <div className="tw:flex tw:flex-wrap tw:gap-2 tw:sm:col-span-12">
            <Button type="button" onClick={form.handleSubmit(onSubmit)}>
              {editIndex !== null && editIndex !== undefined
                ? 'Save Resource'
                : 'Add Resource'}
            </Button>
            <Button type="button" variant="outline" onClick={closeForm}>
              Cancel
            </Button>
          </div>
        </div>
      </FormProvider>
    </div>
  );
};
